import { Typography } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UnitDto } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useUnits } from './api';

/**
 * "Inventory Search" from the header's search menu: the value typed in the search box looked up
 * in inventory (SIM, IMEI, MDN or store), SIMs and devices together. The reference answers with
 * "Total Records- n"; its result columns were not seen (an empty search), so these are the unit
 * list's.
 */
export function InventorySearchPage() {
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Nothing typed: nothing to look up (the reference answers "Total Records- 0").
  const units = useUnits({ q, page, pageSize }, Boolean(q));
  const total = q ? (units.data?.total ?? 0) : 0;
  const columns: Column<UnitDto>[] = [
    { key: '#', header: '#', width: 50, render: (_u, i) => rowNumber(i, page, pageSize) },
    { key: 'sim', header: 'SIM', nowrap: true, render: (u) => u.sim ?? u.paired?.identifier ?? '' },
    { key: 'imei', header: 'IMEI / MEID', nowrap: true, render: (u) => u.imei ?? '' },
    { key: 'mdn', header: 'MDN', nowrap: true, render: (u) => u.mdn ?? '' },
    { key: 'carrier', header: 'Carrier', render: (u) => u.carrier.code },
    { key: 'model', header: 'Model', render: (u) => `${u.model.make} ${u.model.model}` },
    { key: 'status', header: 'Status', render: (u) => u.status },
    { key: 'store', header: 'Store ID', render: (u) => u.owner?.loginId ?? '' },
  ];
  return (
    <>
      <PageHeader title="Inventory Search" />
      <Typography sx={{ mb: 1, fontWeight: 600 }}>Total Records- {total}</Typography>
      <DataTable
        label="Inventory Search"
        columns={columns}
        rows={q ? units.data?.data : []}
        rowKey={(u) => u.id}
        loading={Boolean(q) && units.isFetching}
        error={q ? units.error : null}
        onRetry={() => void units.refetch()}
        emptyText="No Record Found"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </>
  );
}
